import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import api from "../../api"
import { ReactComponent as CalendarIcon } from "../../assets/images/icons/ic-calendar.svg"
import { ReactComponent as CheckIcon } from "../../assets/images/icons/ic-check.svg"
import { ReactComponent as CopyIcon } from "../../assets/images/icons/ic-copy.svg"
import { ReactComponent as DeleteIcon } from "../../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../../assets/images/icons/ic-edit.svg"
import { ReactComponent as ExerciseIcon } from "../../assets/images/icons/ic-exercise-inactive.svg"
import { ReactComponent as QuestionsIcon } from "../../assets/images/icons/ic-questions.svg"
import { ReactComponent as RepeatIcon } from "../../assets/images/icons/ic-repeat.svg"
import { ReactComponent as TrophyIcon } from "../../assets/images/icons/ic-trophy.svg"
import UserPlaceholder from "../../assets/images/placeholders/user-placeholder.png"
import { DialogStatus, TestStatus, TestType } from "../../enums"
import { formatDateV2, formatTimeV2, UTCtoLocal } from "../../utils"
import AlertDialog from "../dialogs/AlertDialog"
import Skeleton from "../Skeleton"
import TooltipWrapper from "../TooltipWrapper"
import styles from "./TestCard.module.css"
import MeatBallsMenu from "../MeatBallsMenu"
import useWindowDimensions from "../../common/hooks/useWindowDimensions"

const TestCard = ({
  test = null,
  loading = false,
  menuOnly = false,
  onDuplicate,
  duplicating = false,
  onEdit = () => { },
  onDelete = () => { },
  onComplete = () => { },
  style = {}
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { width } = useWindowDimensions()

  // delete
  const [openAlert, setOpenAlert] = useState(false)
  const [alertStatus, setAlertStatus] = useState(DialogStatus.Default)
  const [closeTimeout, setCloseTimeout] = useState(null)

  // complete
  const [openCompleteAlert, setOpenCompleteAlert] = useState(false)
  const [completeAlertStatus, setCompleteAlertStatus] = useState(DialogStatus.Default)
  const [completeCloseTimeout, setCompleteCloseTimeout] = useState(null)

  const numberOfTests = useMemo(() => {
    if (!test) {
      return;
    }
    let numberOfTests = 0
    const { innerTests = [] } = test.content || {}
    for (const innerTest of innerTests) {
      const { testType } = innerTest
      switch (testType) {
        case TestType.SingleChoice:
        case TestType.MultipleChoice:
          numberOfTests += 1
          break
        case TestType.TrueFalse:
          numberOfTests += innerTest.answers?.length > 0 ? innerTest.answers.length : 0
          break
        case TestType.TextCompletion:
          if (innerTest.words) {
            numberOfTests += innerTest.words.reduce((a, c) => {
              if (c.hidden) {
                a += 1
              }
              return a
            }, 0)
          }
          break
        default: break;
      }
    }
    return numberOfTests
  }, [test])

  const status = useMemo(() => {
    if (!test) {
      return null;
    }

    if (test.deleted_at) {
      return {
        label: t("tests.status.deleted"),
        color: "#fa6675"
      }
    }

    const { status, published_at, expires_at } = test

    switch (status) {
      case TestStatus.Draft:
        return {
          label: "bozza",
          color: "rgba(var(--text-color-rgb), 24%)"
        }
      case TestStatus.Completed:
        return {
          label: "terminato",
          color: "#64748b"
        }
      case TestStatus.Public:
        if (new Date(published_at) > new Date()) {
          return {
            label: "programmato",
            color: "var(--sf-gray)"
          }
        }
        else if (expires_at && new Date(expires_at) <= new Date()) {
          return {
            label: "scaduto",
            color: "var(--sf-red)"
          }
        }
        else {
          return {
            label: "in corso",
            color: "var(--sf-green)"
          }
        }
      default: return null
    }
  }, [test])

  const menuActions = useMemo(() => {
    if (!test) return []

    const actions = [
      {
        label: t("tests.delete"), icon: DeleteIcon, style: { color: "var(--sf-red)" }, onClick: () => setOpenAlert(true)
      }
    ]
    actions.unshift({
      label: t("tests.edit"), icon: EditIcon, style: { color: "var(--sf-light-green)" }, onClick: onEdit
    },)


    if (test.status === TestStatus.Public) {
      actions.unshift({
        label: t("tests.end"), icon: CheckIcon, style: { color: "var(--sf-yellow)" }, onClick: () => setOpenCompleteAlert(true)
      })
    }

    if (test.status === TestStatus.Public || test.status === TestStatus.Completed) {
      actions.unshift({
        label: t("tests.open"), icon: ExerciseIcon, style: { color: "var(--primary)" }, onClick: () => navigate(`/tests/${test.id}`),
      })
    }

    if (onDuplicate) actions.splice(1, 0, { label: duplicating ? "Duplicazione…" : "Duplica", icon: CopyIcon, onClick: () => { if (!duplicating) onDuplicate(test) } })
    return actions
  }, [test, t, navigate, onEdit, onDuplicate, duplicating])

  const deleteTest = useCallback(async () => {
    try {
      setAlertStatus(DialogStatus.Loading)
      await api.delete(`/admin/tests/${test.id}`)
      onDelete(test.id)
      setAlertStatus(DialogStatus.Success)
      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
      }, 3000))
    } catch (e) {
      console.error(e)
      setAlertStatus(DialogStatus.Error)

      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
      }, 3000))
    }
  }, [test, onDelete])

  const completeTest = useCallback(async () => {
    try {
      setCompleteAlertStatus(DialogStatus.Loading)
      await api.put(`/admin/tests/${test.id}`, { status: TestStatus.Completed })
      onComplete(test.id)
      setCompleteAlertStatus(DialogStatus.Success)
      setCompleteCloseTimeout(setTimeout(() => {
        setOpenCompleteAlert(false)
        setCompleteAlertStatus(DialogStatus.Default)
      }, 3000))
    } catch (e) {
      console.error(e)
      setCompleteAlertStatus(DialogStatus.Error)

      setCompleteCloseTimeout(setTimeout(() => {
        setOpenCompleteAlert(false)
        setCompleteAlertStatus(DialogStatus.Default)
      }, 3000))
    }
  }, [test, onComplete])

  const deleteAlertActions = useMemo(() => {
    return [
      {
        label: t("tests.deleteConfirm").toUpperCase(),
        onClick: deleteTest,
      }
    ]
  }, [deleteTest, t])

  const confirmAlertActions = useMemo(() => {
    return [
      {
        label: t("tests.completeConfirm").toUpperCase(),
        onClick: completeTest,
      }
    ]
  }, [completeTest, t])

  return (
    <div className={menuOnly ? undefined : test?.deleted_at && !loading ? `${styles.container} ${styles.deleted}` : styles.container}
      style={style}>
      {
        !menuOnly && loading === true &&
        <div className={styles.content}>
          <div className={styles.teacher} >
            <Skeleton type="circle" width="48px" height="48px" />
            <div className={styles.column}>
              <Skeleton type="rect" width="146px" height="14px" borderRadius="8px" />
              <Skeleton type="rect" width="196px" height="14px" borderRadius="8px" />
            </div>
          </div>
          <div className={styles.info}>
            <Skeleton type="rect" width="256px" height="14px" borderRadius="8px" />
            <Skeleton type="rect" width="148px" height="14px" borderRadius="8px" />
          </div>
          <div className={styles.right} style={{ justifyContent: "flex-end" }}>
            <Skeleton type="rect" width="64px" height="20px" borderRadius="4px" />
          </div>
        </div>
      }

      {
        !menuOnly && !loading &&
        <div className={styles.content}>
          <div className={styles.teacher} >
            <img className={styles.picture} src={test.course?.thumbnail} alt=""
              onClick={(e) => {
                e.stopPropagation()
                if (["teacher", "tutor"].includes(test.teacher.role)) {
                  navigate(`/users/teachers/${test.teacher.id}`)
                }
                else {
                  navigate(`/users/admins/${test.teacher.id}`)
                }
              }}
            />
            <div className={styles.column}>
              <TooltipWrapper text={test.description}>
                <div className={styles.name}>{test.name}</div>
              </TooltipWrapper>
              <div className={styles.teacherName}
                onClick={() => {
                  if (["teacher", "tutor"].includes(test.teacher.role)) {
                    navigate(`/users/teachers/${test.teacher.id}`)
                  }
                  else {
                    navigate(`/users/admins/${test.teacher.id}`)
                  }
                }}>
                {test.teacher?.name ?? ""} {test.teacher?.surname ?? ""}</div>
            </div>
          </div>
          <div className={styles.info}>
            <div className={styles.value} style={{ maxWidth: "320px" }}>
              {`${test.course?.name ?? "Nessun corso"} - ${test.edition?.name ?? "Nessuna Edizione"}`} - {test.module?.name ?? "Nessun Modulo"}
              {
                test.lesson &&
                ` - ${test.lesson?.name ?? "Nessuna lezione"}`
              }
            </div>
          </div>
          <div className={styles.right}>
            <div className={styles.column}>
              {test.published_at &&
                <TooltipWrapper text="Data di pubblicazione">
                  <div className={styles.questions}>
                    <CalendarIcon />
                    {
                      test.published_at &&
                      `${formatDateV2(test.published_at)} ${formatTimeV2(test.published_at)}`
                    }
                  </div>
                </TooltipWrapper>
              }
              <TooltipWrapper text="Numero di domande">
                <div className={styles.questions}><QuestionsIcon /> {numberOfTests} {(numberOfTests === 0 || numberOfTests > 1) ? t("tests.questions") : t("tests.question")}</div>
              </TooltipWrapper>
            </div>
            <div className={styles.column}>
              <TooltipWrapper text="Data di scadenza">
                <div className={styles.questions}>
                  <CalendarIcon />
                  {
                    test.expires_at &&
                    `${formatDateV2(test.expires_at)} ${formatTimeV2(test.expires_at)}`
                  }
                  {
                    !test.expires_at && "Nessuna scadenza"
                  }

                </div>
              </TooltipWrapper>
              <TooltipWrapper text="Soglia di successo">
                <div className={styles.questions}><TrophyIcon /> {test.success_threshold ?? 0}/{numberOfTests}</div>
              </TooltipWrapper>
            </div>
            <div className={styles.status} style={{ flexGrow: 1, display: "flex", alignItems: "flex-end" }}>
              {status &&
                <div className={styles.badges}>
                  <div className={styles.statusBadge} style={{ "--color": status.color }}>{status.label.toUpperCase()}</div>
                  {test.publishable && test.status === TestStatus.Draft &&
                    <div className={styles.statusBadge} style={{ "--color": "var(--tertiary)" }}>{t("tests.publishable").toUpperCase()}</div>
                  }
                  {!test.publishable && test.status === TestStatus.Draft &&
                    <div className={styles.statusBadge} style={{ "--color": "var(--secondary)" }}>{t("tests.toBeCompleted").toUpperCase()}</div>
                  }
                </div>
              }
            </div>
            <MeatBallsMenu actions={menuActions} style={{ position: 'absolute', top: 0, right: '1rem' }} />
          </div>
        </div>
      }

      {menuOnly && <MeatBallsMenu actions={menuActions} />}
      <AlertDialog appearance={menuOnly ? "modern" : undefined}
        open={openAlert}
        title={t("tests.deleteTitle")}
        text={t("tests.deleteText")}
        status={alertStatus}
        onClose={() => {
          setOpenAlert(false)
          setAlertStatus(DialogStatus.Default)
          if (closeTimeout) {
            clearTimeout(closeTimeout)
          }
        }}
        actions={deleteAlertActions}
      />
      <AlertDialog appearance={menuOnly ? "modern" : undefined}
        open={openCompleteAlert}
        title={t("tests.completeTitle")}
        text={t("tests.completeText")}
        status={completeAlertStatus}
        onClose={() => {
          setOpenCompleteAlert(false)
          setCompleteAlertStatus(DialogStatus.Default)
          if (completeCloseTimeout) {
            clearTimeout(completeCloseTimeout)
          }
        }}
        actions={confirmAlertActions}
      />
    </div >
  )
}

export default TestCard
